import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Read-only display of a creator's socials, shown on their public profile so
 * traders can DYOR before pressing Trade. Verified links show a green tick;
 * unverified links show a subtle warning tag so viewers know to be cautious.
 *
 * Also renders a compact trust score (`{verified} / {total} verified`).
 *
 * When `isOwner` is true and the viewer is looking at their OWN profile with
 * no socials linked yet, we surface a call-to-action linking to /profile
 * so they can add socials without hunting for the setting.
 */
const PLATFORMS = [
  { key: 'x',         label: 'X',         icon: 'x' },
  { key: 'youtube',   label: 'YouTube',   icon: 'youtube' },
  { key: 'discord',   label: 'Discord',   icon: 'discord' },
  { key: 'website',   label: 'Website',   icon: 'globe' },
  { key: 'instagram', label: 'Instagram', icon: 'instagram' },
  { key: 'tiktok',    label: 'TikTok',    icon: 'tiktok' },
  { key: 'linkedin',  label: 'LinkedIn',  icon: 'linkedin' },
];

export default function SocialsDisplay({ socials, isOwner = false }) {
  // Always render all 7 platforms so traders see a full trust checklist,
  // not just a partial list. Each platform lands in one of three states:
  //   - verified: green tick, clickable, opens the URL
  //   - linked:   caution icon, clickable but unverified
  //   - empty:    grayed-out placeholder, non-interactive ("Not linked")
  const items = PLATFORMS.map((p) => {
    const s = socials?.[p.key] || {};
    const url = (s.url || '').trim();
    if (!url) return { ...p, state: 'empty' };
    return { ...p, state: s.verified ? 'verified' : 'linked', url, verified: !!s.verified, followers: s.followers };
  });

  const linkedItems = items.filter((i) => i.state !== 'empty');
  const linkedCount = linkedItems.length;
  const verifiedCount = linkedItems.filter((i) => i.state === 'verified').length;

  // Owner CTA when they have 0 socials at all — nudges them to add.
  if (isOwner && linkedCount === 0) {
    return (
      <div className="socials-display empty owner-cta" data-testid="socials-display-empty-owner">
        <div>
          <b>No socials linked yet.</b><br />
          Add your X, YouTube, Discord, Website, IG, TikTok or LinkedIn so traders know who you are.
        </div>
        <Link
          to="/profile?edit=1&focus=socials"
          className="socials-add-btn"
          data-testid="socials-add-cta"
        >+ Add socials →</Link>
      </div>
    );
  }

  return (
    <div className="socials-display" data-testid="socials-display">
      <div className="socials-display-head">
        <span className="socials-display-title">Socials</span>
        {linkedCount === 0 ? (
          <span
            className="socials-trust none"
            data-testid="socials-trust"
            title="This creator hasn't linked any of the 7 known platforms."
          >
            Not linked
          </span>
        ) : (
          <span
            className={`socials-trust ${verifiedCount === linkedCount ? 'full' : verifiedCount > 0 ? 'partial' : 'none'}`}
            data-testid="socials-trust"
            title={`${verifiedCount} verified of ${linkedCount} linked (${PLATFORMS.length} platforms total)`}
          >
            {verifiedCount} / {linkedCount} verified
          </span>
        )}
      </div>
      <div className="socials-display-grid">
        {items.map((e) => {
          if (e.state === 'empty') {
            return (
              <span
                key={e.key}
                className="socials-btn empty"
                data-testid={`socials-btn-${e.key}-empty`}
                title={`No ${e.label} linked`}
                aria-label={`${e.label} not linked`}
              >
                <SocialIcon k={e.icon} />
                <span className="socials-btn-label">{e.label}</span>
                <span className="socials-btn-empty-tag" aria-hidden="true">—</span>
              </span>
            );
          }
          const verified = e.state === 'verified';
          return (
            <a
              key={e.key}
              href={e.url}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className={`socials-btn${verified ? ' verified' : ''}`}
              data-testid={`socials-btn-${e.key}`}
              title={verified ? `Verified ${e.label} link` : `Unverified ${e.label} link — click to visit`}
            >
              <SocialIcon k={e.icon} />
              {/* Label is redundant with the brand icon on linked/verified pills
                  (e.g. an X logo + a text label "X" reads as two Xs). Kept only
                  in the empty state below where users need to know which platform
                  they'd be adding. */}
              {verified
                ? <span className="socials-btn-tick" aria-label="Verified">✓</span>
                : <span className="socials-btn-caution" aria-label="Unverified">!</span>}
              {typeof e.followers === 'number' && e.followers > 0 && (
                <span className="socials-btn-followers">{compactNumber(e.followers)}</span>
              )}
            </a>
          );
        })}
      </div>
    </div>
  );
}

function compactNumber(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return String(n);
}

function SocialIcon({ k }) {
  const props = { viewBox: '0 0 24 24', width: 15, height: 15, fill: 'currentColor', 'aria-hidden': true };
  switch (k) {
    case 'x':
      return <svg {...props}><path d="M18.244 2H21.5l-7.61 8.706L23 22h-6.797l-5.317-6.964L4.804 22H1.545l8.15-9.32L1 2h6.978l4.802 6.35L18.244 2zm-1.192 18h1.82L7.03 4H5.098l11.954 16z"/></svg>;
    case 'youtube':
      return <svg {...props}><path d="M23.499 6.203a3.008 3.008 0 00-2.117-2.13C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.382.528A3.008 3.008 0 00.501 6.203C0 8.09 0 12 0 12s0 3.91.501 5.797a3.008 3.008 0 002.117 2.13C4.495 20.455 12 20.455 12 20.455s7.505 0 9.382-.528a3.008 3.008 0 002.117-2.13C24 15.91 24 12 24 12s0-3.91-.501-5.797zM9.75 15.568V8.432L15.75 12l-6 3.568z"/></svg>;
    case 'discord':
      return <svg {...props}><path d="M20.317 4.37a19.79 19.79 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.058a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.331c-1.183 0-2.157-1.086-2.157-2.419 0-1.334.955-2.419 2.157-2.419 1.211 0 2.176 1.094 2.157 2.42 0 1.332-.955 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.086-2.157-2.419 0-1.334.955-2.419 2.157-2.419 1.211 0 2.176 1.094 2.157 2.42 0 1.332-.946 2.418-2.157 2.418z"/></svg>;
    case 'globe':
      return <svg {...props} fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18"/></svg>;
    case 'instagram':
      return <svg {...props} fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="0.9" fill="currentColor"/></svg>;
    case 'tiktok':
      return <svg {...props}><path d="M21 8.16a7.06 7.06 0 01-4.35-1.49v6.63a6.11 6.11 0 11-6.11-6.11c.26 0 .51.02.76.05v3.17a2.94 2.94 0 102.35 2.89V2h3.15a4.04 4.04 0 004.2 3.97V8.16z"/></svg>;
    case 'linkedin':
      return <svg {...props}><path d="M4.98 3.5c0 1.38-1.11 2.5-2.49 2.5S0 4.88 0 3.5 1.11 1 2.49 1s2.49 1.12 2.49 2.5zM.24 8h4.49v15H.24V8zm7.14 0h4.3v2.05h.06c.6-1.13 2.06-2.33 4.24-2.33 4.53 0 5.37 2.98 5.37 6.86V23h-4.48v-6.87c0-1.64-.03-3.75-2.29-3.75-2.29 0-2.64 1.79-2.64 3.63V23H7.38V8z"/></svg>;
    default:
      return null;
  }
}
